import { getAlphaLiquid } from "./getAlphaLiquid";
import type { PipeFrictionIntermediates } from "..";
import type { PipeFrictionContext } from "../d";
import { GRAVITY_FT_SEC } from "../../constants";

export enum BeggsBrillFlowRegimes {
  SEGREGATED = "Segregated",
  INTERMITTENT = "Intermittent",
  TRANSITION = "Transition",
  DISTRIBUTED = "Distributed",
  DOWNHILL = "Downhill",
  LIQUID = "Liquid"
}

export type BeggsBrillFlowRegimePropertiesKeys =
  | "areg"
  | "breg"
  | "creg"
  | "d"
  | "e"
  | "f"
  | "g";

export type BeggsBrillFlowRegimeStructure = {
  [key in BeggsBrillFlowRegimePropertiesKeys]: number;
};

// TODO: EVALUATE WHAT IS GOING ON HERE
// Subset of BeggsBrillFlowRegimes that excludes Transition and Liquid
// as they are special and don't have BeggsBrillFlowRegimePropertiesKeys
export type SubsetOfBeggsBrillFlowRegimes = Exclude<
  BeggsBrillFlowRegimes,
  BeggsBrillFlowRegimes.TRANSITION | BeggsBrillFlowRegimes.LIQUID
>;

export type BeggsBrillFlowRegimeProperties = {
  [key in SubsetOfBeggsBrillFlowRegimes]: BeggsBrillFlowRegimeStructure;
};

export function getBeggsBrillFrictionProperties(
  context: PipeFrictionContext
): PipeFrictionIntermediates {
  const {
    phiRad,
    fluidPropertiesContext: {
      fluidProperties: {
        gasViscosityCP,
        liquidSuperficialVelocityFTSEC,
        mixSuperficialVelocityFTSEC,
        gasSuperficialVelocityFTSEC,
        liquidDensityLBMFT3,
        liquidViscosityCP,
        gasDensityLBMFT3
      },
      flowRatesInSitu,
      fluidFrictionProperties: {
        flowRegime,
        froudeNumber,
        liquidInterfacialTension,
        noSlipLiquidHoldupLambdaCL,
        froudeTransitionNumbers
      }
    },
    pipeDiameterIn,
    pipeLengthFt,
    palmerCorrection,
    backpressurePSIA,
    epsilonD
  } = context;

  if (!flowRatesInSitu) throw new Error("flowRatesInSitu not defined");

  // const vals = Object.keys(BeggsBrillFlowRegimes);
  // if (vals.indexOf(flowRegime.toUpperCase()) === -1) {
  //   throw new Error("Flow regime must be a BeggsBrill variant");
  // }

  // D185-202
  const { liquidAlpha } = getAlphaLiquid(
    liquidSuperficialVelocityFTSEC,
    liquidDensityLBMFT3,
    liquidInterfacialTension,
    noSlipLiquidHoldupLambdaCL,
    froudeNumber,
    phiRad,
    flowRegime as BeggsBrillFlowRegimes,
    froudeTransitionNumbers
  );

  // CONDITIONALLY APPLY PALMER CORRECTION
  let correctedNoSlipLiquidHoldupLambdaCL = noSlipLiquidHoldupLambdaCL;
  if (palmerCorrection) {
    if (Math.abs(phiRad) > 0.0001) {
      if (phiRad < 0) {
        // Downhill
        correctedNoSlipLiquidHoldupLambdaCL =
          correctedNoSlipLiquidHoldupLambdaCL * 0.685;
      } else {
        // Uphill
        correctedNoSlipLiquidHoldupLambdaCL =
          correctedNoSlipLiquidHoldupLambdaCL * 0.924;
      }
    }
  }

  // D236: =D63*D153+D62*(1-D153)
  const noSlipDensityLBFT3 =
    liquidDensityLBMFT3 * correctedNoSlipLiquidHoldupLambdaCL +
    gasDensityLBMFT3 * (1 - correctedNoSlipLiquidHoldupLambdaCL);

  // // u_no_slip
  // D238: =D130*D231+D129*(1-D231)
  const noSlipViscosityCP =
    liquidViscosityCP * liquidAlpha + gasViscosityCP * (1 - liquidAlpha);

  // // Re no-slip
  // D240: =(1488*D236*D151*(D132/12)/D238)
  const noSlipReynoldsNumber =
    (1488 *
      noSlipDensityLBFT3 *
      mixSuperficialVelocityFTSEC *
      (pipeDiameterIn / 12)) /
    noSlipViscosityCP;

  // y
  // D245: =D153/(D231^2)
  const y = correctedNoSlipLiquidHoldupLambdaCL / liquidAlpha ** 2;

  // // s1
  // D246 =LN(2.2*D245-1.2)
  const s1 = Math.log10(2.2 * y - 1.2);

  // // s2
  // D247: =(LN(D245)/(-0.0523+3.182*LN(D245)-0.8725*(LN(D245)^2)+0.01853*(LN(D245)^4)))
  const s2 =
    Math.log(y) /
    (-0.0523 +
      3.182 * Math.log(y) -
      0.8725 * Math.log(y) ** 2 +
      0.01853 * Math.log(y) ** 4);

  // // S
  // D249: =IF(AND(1<D245,D245<1.2),D246,IF(D245=0,0,D247))
  const S = y > 1 && y < 1.2 ? s1 : s2;

  // // Friction Factor Ratio
  // D250: =EXP(D249)
  const frictionFactorRatio = Math.exp(S);

  // // Chen f_no slip
  // D254: =(1/(-2*LOG10(D252/3.7065-(5.0452/D240)*LOG10(((D252^1.1098)/2.8257)+(5.8506/(D240^0.8981))))))^2
  const chenBeggsBrill =
    (1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / noSlipReynoldsNumber) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / noSlipReynoldsNumber ** 0.8981
              )
        ))) **
    2;

  // // Two Phase Friction Factor
  // D256: =D254*D250
  const twoPhaseFrictionFactor = chenBeggsBrill * frictionFactorRatio;

  // // Friction Pressure Gradient dpf/dz
  // D259: =(D256*D236*(D151^2))/(144*2*D132)*D3
  const frictionPressurePSIA =
    ((2 *
      twoPhaseFrictionFactor *
      noSlipDensityLBFT3 *
      mixSuperficialVelocityFTSEC ** 2) /
      (144 * GRAVITY_FT_SEC * pipeDiameterIn)) *
    pipeLengthFt;

  // D263: =((D233*62.428)/144)*D3*SIN(D8)
  // const hydrostaticPressurePSIA =
  // ((liquidAlpha * liquidDensityLBMFT3 +
  //   (1 - liquidAlpha) * gasDensityLBMFT3) /
  //   144) *
  // pipeLengthFt *
  // Math.sin(phiRad);

  // Beggs-specific fluid spgr calc
  const beggsFluidSpecificGravity =
    (liquidAlpha * liquidDensityLBMFT3 + (1 - liquidAlpha) * gasDensityLBMFT3) /
    62.428;
  // const hydrostaticPressurePSIA =
  //   ((beggsFluidSpecificGravity * 62.428) / 144) * hydrostaticSum;

  const frictionProperties: PipeFrictionIntermediates = {
    frictionPressurePSIA,
    backpressurePSIA,
    froudeNumber,
    noSlipDensityLBFT3,
    correctedNoSlipLiquidHoldupLambdaCL,
    froudeTransitionNumbers,
    liquidAlpha,
    beggsFluidSpecificGravity,
    noSlipViscosityCP,
    noSlipReynoldsNumber,
    y,
    s1,
    s2,
    S,
    frictionFactorRatio,
    epsilonD,
    chenBeggsBrill,
    twoPhaseFrictionFactor,
    liquidSuperficialVelocityFTSEC,
    mixSuperficialVelocityFTSEC,
    gasSuperficialVelocityFTSEC
  };

  return frictionProperties;
}
